import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  createTalkingPoint,
  refreshTalkingPoints,
  reorderTalkingPoints,
  replaceTalkingPoints,
  updateTalkingPoint,
} from "@/services/frontendServices/talkingPointServices";
import type {
  MeetingPrepDto,
  PrepSource,
  RefreshTalkingPointsResponseDto,
  ReplaceTalkingPointsPayload,
  TalkingPointDto,
} from "@/types";

import { prepKeys, prepSourceKey } from "./usePrep";

// Re-exported so the existing hooks-level import path keeps working; the
// predicate itself now lives beside TALKING_POINTS_IN_FLIGHT_STATUSES in
// @/enums, since it is a pure function of that constant, not a hook.
export { isTalkingPointsGenerating } from "@/enums";

/**
 * Appends a point without refetching: the endpoint returns the created row, and
 * the prep query is the section's only source of truth, so the row is spliced
 * into the cached prep at the position the server assigned it.
 */
export const useCreateTalkingPoint = (source: PrepSource) => {
  const queryClient = useQueryClient();

  return useMutation<
    TalkingPointDto,
    Error,
    { prepId: string; content: string }
  >({
    mutationFn: ({ prepId, content }) => createTalkingPoint(prepId, content),
    onSuccess: (point) => {
      queryClient.setQueryData<MeetingPrepDto>(
        prepKeys.detail(prepSourceKey(source)),
        (prep) =>
          prep
            ? { ...prep, talkingPoints: [...prep.talkingPoints, point] }
            : prep
      );
    },
  });
};

type CheckFields = Pick<
  TalkingPointDto,
  "checked" | "checkedAt" | "checkedOrigin" | "checkedEvidence"
>;

function checkFields(point: TalkingPointDto): CheckFields {
  return {
    checked: point.checked,
    checkedAt: point.checkedAt,
    checkedOrigin: point.checkedOrigin,
    checkedEvidence: point.checkedEvidence,
  };
}

function sameCheck(a: CheckFields, b: CheckFields): boolean {
  return (
    a.checked === b.checked &&
    a.checkedAt === b.checkedAt &&
    a.checkedOrigin === b.checkedOrigin &&
    a.checkedEvidence === b.checkedEvidence
  );
}

/**
 * Carries coverage across the Customize save only where the cache learned it
 * while the save was in flight. The endpoint never writes `checked` but
 * re-reads every row to answer, so a check reply that landed after that read
 * is newer than the save's copy — adopting the save's copy would un-tick the
 * box in front of the rep. Everywhere else the reply is the fresher read and
 * wins, which is how an AI check the bg-agent wrote after the cache was loaded
 * reaches the screen: nothing else refetches once generation has settled.
 * Toggles still inside their debounce never reach the cache; they live in
 * `useTalkingPointChecks`, which renders them over whatever this writes.
 */
function keepLocalChecks(
  cached: TalkingPointDto[],
  saved: TalkingPointDto[],
  checksAtSend: Map<string, CheckFields>
): TalkingPointDto[] {
  const previousById = new Map(cached.map((point) => [point.id, point]));
  return saved.map((point) => {
    const previous = previousById.get(point.id);
    if (!previous) return point;
    const atSend = checksAtSend.get(point.id);
    return atSend && sameCheck(atSend, previous)
      ? point
      : { ...point, ...checkFields(previous) };
  });
}

/**
 * Reorders `points` by `idOrder`, appending whatever `idOrder` does not name
 * at the end instead of dropping it. The one seam a reorder is allowed to
 * touch is sequence: an id list is authoritative for order, never for which
 * points exist or what they contain. Shared between the optimistic override
 * in `useTalkingPointOrder` (order the rep hasn't gotten a response for yet)
 * and the reorder mutation below (order the server answered with, applied to
 * the client's own copies of the points) — same shape, different source of
 * truth for the sequence.
 */
export function reorderByIds(
  points: TalkingPointDto[],
  idOrder: string[]
): TalkingPointDto[] {
  const byId = new Map(points.map((point) => [point.id, point]));
  const named = idOrder.flatMap((id) => {
    const point = byId.get(id);
    return point ? [point] : [];
  });
  const rest = points.filter((point) => !idOrder.includes(point.id));
  return [...named, ...rest];
}

/**
 * The Customize save. The response is the whole prep but the request only owns
 * the list, so only the list is taken — plus `talkingPointsSourcesChanged`,
 * which the route deliberately recomputes *after* the write because deleting an
 * AI point drops the citation that made the prep look stale. Everything else in
 * the reply is a snapshot that may predate a goal save or a note autosave still
 * in flight, and writing it back would revert them.
 */
export const useReplaceTalkingPoints = (source: PrepSource) => {
  const queryClient = useQueryClient();

  return useMutation<
    MeetingPrepDto,
    Error,
    { prepId: string; payload: ReplaceTalkingPointsPayload },
    { checksAtSend: Map<string, CheckFields> }
  >({
    mutationFn: ({ prepId, payload }) => replaceTalkingPoints(prepId, payload),
    onMutate: () => {
      const cached = queryClient.getQueryData<MeetingPrepDto>(
        prepKeys.detail(prepSourceKey(source))
      );
      return {
        checksAtSend: new Map(
          (cached?.talkingPoints ?? []).map((point) => [
            point.id,
            checkFields(point),
          ])
        ),
      };
    },
    onSuccess: (prep, _variables, context) => {
      queryClient.setQueryData<MeetingPrepDto>(
        prepKeys.detail(prepSourceKey(source)),
        (cached) =>
          cached
            ? {
                ...cached,
                talkingPoints: keepLocalChecks(
                  cached.talkingPoints,
                  prep.talkingPoints,
                  context?.checksAtSend ?? new Map()
                ),
                talkingPointsSourcesChanged: prep.talkingPointsSourcesChanged,
              }
            : prep
      );
    },
  });
};

/**
 * The drag-and-drop reorder. Answers `{ talkingPoints }`, not the whole prep,
 * and the request it answers for changed exactly one thing: `position`. So the
 * response is authoritative for order and nothing else — not `checked` (the
 * live checklist owns it and the endpoint re-reads rows to answer, same
 * staleness hazard as the Customize save) and not membership (a point added or
 * edited elsewhere while the PUT was in flight is invisible to a response
 * built from the ids the drag started with, and dropping it here would lose
 * it for good — the prep query only polls while generation is in flight).
 * `reorderByIds` reorders the client's own copies of the points by the
 * response's id sequence, so anything the response doesn't know about simply
 * keeps existing.
 */
export const useReorderTalkingPoints = (source: PrepSource) => {
  const queryClient = useQueryClient();

  return useMutation<
    { talkingPoints: TalkingPointDto[] },
    Error,
    { prepId: string; talkingPointIds: string[] }
  >({
    mutationFn: ({ prepId, talkingPointIds }) =>
      reorderTalkingPoints(prepId, talkingPointIds),
    onSuccess: (result) => {
      queryClient.setQueryData<MeetingPrepDto>(
        prepKeys.detail(prepSourceKey(source)),
        (cached) =>
          cached
            ? {
                ...cached,
                talkingPoints: reorderByIds(
                  cached.talkingPoints,
                  result.talkingPoints.map((point) => point.id)
                ),
              }
            : cached
      );
    },
  });
};

/**
 * Requests a regeneration and adopts the status the server reports. 202 does not
 * mean a run started — the endpoint is idempotent, and an org with generation
 * disabled gets its prep back untouched — so the caller must read the status
 * rather than assume one is now in flight.
 */
export const useRefreshTalkingPoints = (source: PrepSource) => {
  const queryClient = useQueryClient();

  return useMutation<RefreshTalkingPointsResponseDto, Error, string>({
    mutationFn: (prepId) => refreshTalkingPoints(prepId),
    onSuccess: (result) => {
      queryClient.setQueryData<MeetingPrepDto>(
        prepKeys.detail(prepSourceKey(source)),
        (prep) =>
          prep
            ? {
                ...prep,
                talkingPointsStatus: result.talkingPointsStatus,
                talkingPointsRequestedAt: result.talkingPointsRequestedAt,
              }
            : prep
      );
    },
  });
};

/**
 * The live-checklist toggle. Invalidates nothing: a refetch of the whole prep
 * in the middle of a call is exactly what we are avoiding, and checking a point
 * moves neither the schedule CTA nor the account's prep summary.
 *
 * The request sends `checked` alone, so only coverage is taken from the reply.
 * The endpoint re-reads the row to answer, and the rest of that row — content,
 * position, origin and the source chip — is a snapshot that may predate a
 * Customize save or a reorder, both of which write fields this reply carries.
 * `keepLocalChecks` guards the same overlap from the other side.
 */
export const useUpdateTalkingPoint = (source: PrepSource) => {
  const queryClient = useQueryClient();

  return useMutation<
    TalkingPointDto,
    Error,
    { pointId: string; checked: boolean }
  >({
    mutationFn: ({ pointId, checked }) =>
      updateTalkingPoint(pointId, { checked }),
    onSuccess: (point) => {
      queryClient.setQueryData<MeetingPrepDto>(
        prepKeys.detail(prepSourceKey(source)),
        (prep) =>
          prep
            ? {
                ...prep,
                talkingPoints: prep.talkingPoints.map((current) =>
                  current.id === point.id
                    ? {
                        ...current,
                        checked: point.checked,
                        checkedAt: point.checkedAt,
                        checkedOrigin: point.checkedOrigin,
                        checkedEvidence: point.checkedEvidence,
                      }
                    : current
                ),
              }
            : prep
      );
    },
  });
};
